/* eslint-disable no-param-reassign */
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import type React from 'react';
import type { AppThunk } from '../app/redux/getReduxStore';
import type ToastComponent from '../components/Toast';
import {
  DraftsOrder,
  ExternalPostsOrder,
  TopicPostsOrder,
} from '../graphql/generated/graphql';
import { DEFAULT_EMOJI_NAMES, getEmojiItemByChar } from '../utils/emoji';
import { persistReducer } from '../utils/persistRedux';

type ViewState<Props> = {
  isOpen: boolean;
  props: Props;
  View: React.ElementType | null;
};

type ModalProps = Record<string, unknown> & {
  close?: () => void;
  disableOutsideClose?: boolean;
};

export type ToastLevel = 'error' | 'warning' | 'success';

type ToastRenderFunction = (props: {
  onDismiss: () => void;
}) => React.ReactNode;

interface Toast {
  id: string;
  render:
    | ToastRenderFunction
    | Omit<React.ComponentProps<typeof ToastComponent>, 'onDismiss'>;
}

export interface UIState {
  collapsedSidebarSections: Record<string, boolean>;
  descriptionCollapsedTopics: Record<string, boolean | undefined>;
  dismissedSearchConnectionPrompts: string[];
  draftsOrder: DraftsOrder;
  emojiSkinToneModifier: string | null;
  externalPostsOrder: ExternalPostsOrder;
  externalTopbarLoginRedirectPath?: string;
  hiddenMentionIntegrationPrompts: Record<string, boolean | undefined>;
  isHelpMenuOpen: boolean;
  isHelpMenuVisible: boolean;
  isIntegrationBannerHidden: boolean;
  isOrganizationSummaryCollapsed: boolean;
  isVersionMismatchModalOpen: boolean;
  isStickyTOCPreferred: boolean;
  isSidebarOpenPreferred: boolean;
  isUserProfileCollapsed: boolean;
  lastCodeBlockSyntax: string | null;
  modal: ViewState<ModalProps>;
  recentlyUsedEmojis: string[];
  recentlyViewedPostIds: string[];
  recentlyUsedTemplateIds: string[];
  recentlyMentionedUserIds: string[];
  rightPanel: ViewState<Record<string, unknown>>;
  selectedSectionId: string | null;
  templatesOrderBy: TopicPostsOrder;
  toasts: Toast[];
  topicPostsOrderBy: Record<string, TopicPostsOrder>;
}

const initialState: UIState = {
  collapsedSidebarSections: {},
  descriptionCollapsedTopics: {},
  dismissedSearchConnectionPrompts: [],
  draftsOrder: DraftsOrder.RecentlyViewed,
  emojiSkinToneModifier: null,
  externalPostsOrder: ExternalPostsOrder.Popularity,
  externalTopbarLoginRedirectPath: undefined,
  hiddenMentionIntegrationPrompts: {},
  isHelpMenuOpen: false,
  isHelpMenuVisible: true,
  isIntegrationBannerHidden: false,
  isOrganizationSummaryCollapsed: false,
  isVersionMismatchModalOpen: false,
  isStickyTOCPreferred: true,
  isSidebarOpenPreferred: false,
  isUserProfileCollapsed: false,
  lastCodeBlockSyntax: null,
  modal: {
    isOpen: false,
    props: {},
    View: null,
  },
  recentlyMentionedUserIds: [],
  recentlyUsedEmojis: DEFAULT_EMOJI_NAMES,
  recentlyUsedTemplateIds: [],
  recentlyViewedPostIds: [],
  rightPanel: {
    isOpen: false,
    props: {},
    View: null,
  },
  selectedSectionId: null,
  templatesOrderBy: TopicPostsOrder.Popularity,
  toasts: [],
  topicPostsOrderBy: {},
};

const uiSlice = createSlice({
  name: 'uiSlice',
  initialState,
  reducers: {
    openVersionMismatchModal: (state) => {
      state.isVersionMismatchModalOpen = true;
    },
    openModal: (
      state,
      action: PayloadAction<{
        View: React.ElementType;
        props?: Record<string, unknown>;
      }>,
    ) => {
      state.modal = {
        isOpen: true,
        View: action.payload.View,
        props: action.payload.props ?? {},
      };
    },
    closeModal: (
      state,
      action: PayloadAction<{ reset: boolean } | undefined>,
    ) => {
      if (action.payload?.reset) {
        state.modal = initialState.modal;
      } else {
        state.modal.isOpen = false;
      }
    },
    openRightPanel: (
      state,
      action: PayloadAction<{
        View: React.ElementType;
        props?: Record<string, unknown>;
      }>,
    ) => {
      state.rightPanel = {
        isOpen: true,
        View: action.payload.View,
        props: action.payload.props ?? {},
      };
    },
    closeRightPanel: (
      state,
      action: PayloadAction<{ reset: boolean } | undefined>,
    ) => {
      if (action.payload?.reset) {
        state.rightPanel = initialState.rightPanel;
      } else {
        state.rightPanel.isOpen = false;
      }
    },
    toggleSidebar: (
      state,
      { payload: forceValue }: PayloadAction<boolean | undefined>,
    ) => {
      state.isSidebarOpenPreferred =
        forceValue !== undefined ? forceValue : !state.isSidebarOpenPreferred;
    },
    setSelectedSectionId: (
      state,
      { payload: selectedSectionId }: PayloadAction<string | null>,
    ) => {
      state.selectedSectionId = selectedSectionId;
    },
    hideIntegrationBanner: (state) => {
      state.isIntegrationBannerHidden = true;
    },
    setStickyTOCPreferred: (
      state,
      { payload: isStickyTOCPreferred }: PayloadAction<boolean>,
    ) => {
      state.isStickyTOCPreferred = isStickyTOCPreferred;
    },
    toggleOrganizationSummaryCollapsed: (
      state,
      { payload: isCollapsed }: PayloadAction<boolean>,
    ) => {
      state.isOrganizationSummaryCollapsed = isCollapsed;
    },
    changeHelpMenuOpen: (
      state,
      { payload: isOpen }: PayloadAction<boolean>,
    ) => {
      state.isHelpMenuOpen = isOpen;
    },
    changeHelpMenuVisible: (
      state,
      { payload: isVisible }: PayloadAction<boolean>,
    ) => {
      state.isHelpMenuVisible = isVisible;
    },
    toggleUserProfileCollapsed: (
      state,
      { payload: isCollapsed }: PayloadAction<boolean>,
    ) => {
      state.isUserProfileCollapsed = isCollapsed;
    },
    addToRecentlyUsedEmojis: (
      state,
      { payload: char }: PayloadAction<string>,
    ) => {
      const emoji = getEmojiItemByChar(char);
      if (emoji) {
        const HISTORY_MAX_LENGTH = 100;

        state.recentlyUsedEmojis = [
          emoji.name,
          ...state.recentlyUsedEmojis,
        ].slice(0, HISTORY_MAX_LENGTH);
      }
    },
    addToRecentlyViewedPostIds: (
      state,
      { payload: postIds }: PayloadAction<string[]>,
    ) => {
      const HISTORY_MAX_LENGTH = 25;

      state.recentlyViewedPostIds = [
        ...postIds,
        ...state.recentlyViewedPostIds,
      ].slice(0, HISTORY_MAX_LENGTH);
    },
    removeFromRecentlyViewedPostIds: (
      state,
      { payload: postId }: PayloadAction<string>,
    ) => {
      state.recentlyViewedPostIds = state.recentlyViewedPostIds.filter(
        (id) => id !== postId,
      );
    },
    addUsedTemplate: (
      state,
      { payload: templateId }: PayloadAction<string>,
    ) => {
      const HISTORY_MAX_LENGTH = 10;

      state.recentlyUsedTemplateIds = [
        templateId,
        ...state.recentlyUsedTemplateIds,
      ].slice(0, HISTORY_MAX_LENGTH);
    },
    addMentionedUser: (state, { payload: userId }: PayloadAction<string>) => {
      const HISTORY_MAX_LENGTH = 25;

      state.recentlyMentionedUserIds = _.uniq([
        userId,
        ...state.recentlyMentionedUserIds,
      ]).slice(0, HISTORY_MAX_LENGTH);
    },
    changeEmojiSkinToneModifier: (
      state,
      { payload: modifier }: PayloadAction<string | null>,
    ) => {
      state.emojiSkinToneModifier = modifier;
    },
    setDraftsOrder: (
      state,
      { payload: draftsOrder }: PayloadAction<DraftsOrder>,
    ) => {
      state.draftsOrder = draftsOrder;
    },
    setExternalPostsOrder: (
      state,
      { payload: externalPostsOrder }: PayloadAction<ExternalPostsOrder>,
    ) => {
      state.externalPostsOrder = externalPostsOrder;
    },
    setExternalTopbarLoginRedirectPath: (
      state,
      {
        payload: externalTopbarLoginRedirectPath,
      }: PayloadAction<string | undefined>,
    ) => {
      state.externalTopbarLoginRedirectPath = externalTopbarLoginRedirectPath;
    },
    dismissSearchConnectionPrompt: (
      state,
      { payload: platform }: PayloadAction<string>,
    ) => {
      state.dismissedSearchConnectionPrompts = [
        ...state.dismissedSearchConnectionPrompts,
        platform,
      ];
    },
    changeTemplatesOrderBy: (
      state,
      { payload: templatesOrderBy }: PayloadAction<TopicPostsOrder>,
    ) => {
      state.templatesOrderBy = templatesOrderBy;
    },
    changeCodeBlockSyntax: (
      state,
      { payload: syntax }: PayloadAction<string | null>,
    ) => {
      state.lastCodeBlockSyntax = syntax;
    },
    changeTopicPostsOrderBy: (
      state,
      action: PayloadAction<{
        topicId: string;
        orderBy: TopicPostsOrder;
      }>,
    ) => {
      state.topicPostsOrderBy[action.payload.topicId] = action.payload.orderBy;
    },
    toggleSidebarSection: (
      state,
      {
        payload: { section, isInitiallyCollapsed },
      }: PayloadAction<{ section: string; isInitiallyCollapsed?: boolean }>,
    ) => {
      state.collapsedSidebarSections[section] =
        section in state.collapsedSidebarSections
          ? !state.collapsedSidebarSections[section]
          : !isInitiallyCollapsed;
    },
    toggleTopicDescription: (state, { payload: id }: PayloadAction<string>) => {
      if (state.descriptionCollapsedTopics[id]) {
        delete state.descriptionCollapsedTopics[id];
      } else {
        state.descriptionCollapsedTopics[id] = true;
      }
    },
    hideMentionIntegrationPrompt: (
      state,
      { payload: platform }: PayloadAction<string>,
    ) => {
      state.hiddenMentionIntegrationPrompts[platform] = true;
    },
    clearToasts: (state) => {
      state.toasts = [];
    },
    removeToast: (state, { payload: id }: PayloadAction<string>) => {
      state.toasts = state.toasts.filter((toast) => toast.id !== id);
    },
    pushToast: (state, { payload: toast }: PayloadAction<Toast>) => {
      state.toasts.push(toast);
    },
  },
});

const pushToast =
  (render: Toast['render'], duration = 5000): AppThunk =>
  (dispatch) => {
    const id = Math.random().toString(36).slice(-4);
    if (duration > 0) {
      setTimeout(() => {
        dispatch(UIActions.removeToast(id));
      }, duration);
    }
    dispatch(uiSlice.actions.pushToast({ id, render }));
    return Promise.resolve(id);
  };

const openRightPanel =
  <ViewType extends React.ElementType>(
    View: ViewType,
    props?: Omit<React.ComponentProps<ViewType>, 'close'>,
  ): AppThunk =>
  (dispatch) => {
    dispatch(uiSlice.actions.openRightPanel({ View, props }));
  };

const openModal =
  <ViewType extends React.ElementType>(
    View: ViewType,
    props?: Omit<React.ComponentProps<ViewType>, 'close'> & {
      close?: () => void;
      disableOutsideClose?: boolean;
    },
  ): AppThunk =>
  (dispatch) => {
    dispatch(uiSlice.actions.openModal({ View, props }));
  };

export const UIActions = {
  ...uiSlice.actions,
  openRightPanel,
  openModal,
  pushToast,
};

export default persistReducer(
  {
    key: 'ui',
    whitelist: [
      'collapsedSidebarSections',
      'descriptionCollapsedTopics',
      'dismissedSearchConnectionPrompts',
      'draftsOrder',
      'emojiSkinToneModifier',
      'externalPostsOrder',
      'hiddenMentionIntegrationPrompts',
      'isOrganizationSummaryCollapsed',
      'isStickyTOCPreferred',
      'isUserProfileCollapsed',
      'lastCodeBlockSyntax',
      'recentlyMentionedUserIds',
      'recentlyUsedEmojis',
      'recentlyUsedTemplateIds',
      'recentlyViewedPostIds',
      'templatesOrderBy',
      'topicPostsOrderBy',
    ],
    migrate: (state) =>
      Promise.resolve(
        state &&
          (state.recentlyUsedEmojis?.length
            ? state
            : {
                ...state,
                recentlyUsedEmojis: DEFAULT_EMOJI_NAMES,
              }),
      ),
  },
  uiSlice.reducer,
);
